import nativeBridge from "./nativeBridge";
import {SocialSignInProvider} from "../client/models/authentication/SocialSignInProvider";

export type LoginAnalyticsMethod = "password" | SocialSignInProvider;

/**
 * Uniquely identify buttons in the app for analytics.
 * A description for each button identifier clarifies its purpose.
 * Use kebab-case for naming.
 */
export type ButtonIdentifier =
  // Onboarding Intro (onboarding/info)
  | 'onboarding-info-skip-intro' // Skips the intro carousel and advances to account creation.
  | 'onboarding-info-next' // Advances to the next intro slide, or to account creation on the last slide.

  // Create Account, legacy in-app form (onboarding/create-account)
  | 'create-account-submit' // Submits the in-app create-account form (legacy 14-day trial).
  | 'create-account-sign-in' // Navigates from the legacy create-account page to the Sign In screen.
  | 'create-account-terms' // Opens the Terms of Use page on the website.
  | 'create-account-privacy' // Opens the Privacy Policy page on the website.
  | 'create-account-license' // Opens the License Agreement page on the website.

  // Create Trial Account, the Sign-in/Create-Account choice screen (onboarding/create-trial-account)
  | 'create-account-via-website' // Redirects to the website to create a seven-day-trial account.
  | 'create-trial-account-sign-in' // Navigates from the trial choice screen to the Sign In screen.

  // Disclosure and Consent (onboarding/disclosure-and-consent)
  | 'disclosure-and-consent-allow' // Grants prominent disclosure consent.
  | 'disclosure-and-consent-dont-allow' // Declines prominent disclosure consent.

  // Keyboard Wizard (onboarding/keyboard-wizard)
  | 'keyboard-wizard-choose-device-back' // Back action on the choose-device step.
  | 'keyboard-wizard-choose-device-help' // Opens keyboard connection help on the choose-device step.
  | 'keyboard-wizard-choose-device-setup-later' // Skips keyboard setup from the choose-device step.
  | 'keyboard-wizard-choose-device-next' // Advances from the choose-device step to set keyboard size.
  | 'keyboard-wizard-set-size-back' // Back action on the set-keyboard-size step.
  | 'keyboard-wizard-set-size-help' // Opens keyboard connection help on the set-keyboard-size step.
  | 'keyboard-wizard-complete-back' // Back action on the setup-complete step.
  | 'keyboard-wizard-complete-start-playing' // Completes the wizard and starts playing.
  | 'keyboard-wizard-wrong-device-modal-close' // Dismisses the wrong-device-detected modal.
  | 'keyboard-wizard-wrong-device-modal-switch' // Switches to the active device in the wrong-device-detected modal.
  | 'keyboard-wizard-wrong-device-modal-dont-switch' // Keeps the configured device in the wrong-device-detected modal.

  // Set Up Your Keyboard (onboarding/setup-your-keyboard)
  | 'setup-your-keyboard-back' // Back action on the set-up-your-keyboard screen.
  | 'setup-your-keyboard-start-setup' // Starts the keyboard wizard.
  | 'setup-your-keyboard-setup-later' // Defers keyboard setup.

  // Set Up Keyboard Later (onboarding/setup-keyboard-later)
  | 'setup-keyboard-later-back' // Back action on the set-up-keyboard-later screen.
  | 'setup-keyboard-later-start-playing' // Completes onboarding and starts playing.

  // Set Up Feedback And Scoring (onboarding/setup-feedback-and-scoring)
  | 'setup-feedback-back' // Back action on the feedback-and-scoring screen.
  | 'setup-feedback-use-cable' // Chooses cable/MIDI-based scoring.
  | 'setup-feedback-use-microphone' // Chooses microphone-based (pitch detection) scoring.

  // Allow Microphone Access (onboarding/setup-feedback-allow-microphone-access)
  | 'allow-microphone-access-back' // Back action on the allow-microphone-access screen.
  | 'allow-microphone-access-request' // Requests microphone access from the OS.

  // Mic Access Needed (onboarding/setup-feedback-mic-access-needed)
  | 'mic-access-needed-back' // Back action on the mic-access-needed screen.
  | 'mic-access-needed-help' // Opens microphone access help.
  | 'mic-access-needed-continue-without-mic' // Completes onboarding without microphone access.

  // You Are All Set (onboarding/setup-feedback-you-are-all-set)
  | 'you-are-all-set-back' // Back action on the you-are-all-set screen.
  | 'you-are-all-set-start-playing' // Completes onboarding and starts playing.

  // Pitch Detection Selected (onboarding/pitch-detection-selected)
  | 'pitch-detection-selected-back' // Back action on the pitch-detection-selected screen.
  | 'pitch-detection-selected-start-playing' // Completes onboarding and starts playing.
;

/**
 * Context shared by several analytics events, identifying the screen the event
 * originated from. `screenClass` is the stable screen-catalog id (e.g.
 * "onboarding/create-trial-account"), mirroring the `screen_class` parameter of
 * the in_app_page_view event.
 */
export interface IScreenContext {
  screenClass: string;
}

/** Where a tap leads the user. */
type TapDestination = 'website' | 'in_app';

/**
 * A centralized service for sending analytics events from the app to analytics services via the native container.
 * Adding new analytics events here helps keep track of what events are being sent,
 * and ensures consistency in event naming and parameters.
 *
 * Eventually, this may be encapsulated by a hook, if future dependencies warrant it.
 *
 * NAMING CONVENTIONS:
 *   Use singular, present tense, and the subject proceeds the action.
 * Method names
 *   - Follow the pattern on[Subject]_[Action].
 *   - e.g., onButton_press.
 * Event names
 *   - Use snake case.
 *   - e.g., button_press.
 */
class InAppAnalyticsService {
  /**
   * @param buttonIdentifier Uniquely identifies the button. Potentially used in conversion funnels.
   * @param context Optional screen context (screen_class) for the screen the button is on.
   */
  onButton_press(buttonIdentifier: ButtonIdentifier, context?: IScreenContext) {
    nativeBridge.sendAnalyticsEvent('button_press', {
      buttonIdentifier,
      ...(context ? { screen_class: context.screenClass } : {}),
    });
  }

  /**
   * Fires on every screen navigation within the app. The two parameters are
   * taken from the app's screen catalog: `screen_name` is human-readable and may
   * change; `screen_class` is the stable id for the semantically same screen.
   */
  onInAppPage_view(context: { screenName: string; screenClass: string }) {
    nativeBridge.sendAnalyticsEvent('in_app_page_view', {
      screen_name: context.screenName,
      screen_class: context.screenClass,
    });
  }

  /**
   * Marks the start of the Create Account funnel. Fires when the user taps the
   * "Create Free Account" / "Create Account" button on a Sign-in/Create-Account
   * choice screen. `destination` distinguishes the website sign-up flow from the
   * legacy in-app sign-up form.
   */
  onCreateAccount_tap(context: IScreenContext & { destination: TapDestination }) {
    nativeBridge.sendAnalyticsEvent('create_account_tap', {
      screen_class: context.screenClass,
      destination: context.destination,
    });
  }

  /**
   * Marks the start of the Sign-in funnel. Fires when the user taps "Sign In" on
   * a Sign-in/Create-Account choice screen. Both choice screens navigate to the
   * in-app Sign In screen, so the destination is always in_app.
   */
  onSignIn_tap(context: IScreenContext) {
    const destination: TapDestination = 'in_app';
    nativeBridge.sendAnalyticsEvent('sign_in_tap', {
      screen_class: context.screenClass,
      destination,
    });
  }

  /**
   * GA4 recommended login event. Fires on the App stream after successful
   * sign-in. Must be called after native `setUserId` (via `user.signedIn`) so
   * the event is associated with the user id — not as an event parameter.
   */
  onLogin_success(method: LoginAnalyticsMethod = "password") {
    if (!nativeBridge.isRunningInNativeContainer) {
      return;
    }

    nativeBridge.sendAnalyticsEvent('login', { method });
  }

  async getAppInstanceID(): Promise<string | null> {
    if (!nativeBridge.isRunningInNativeContainer) {
      return null;
    }
    return await nativeBridge.getAppInstanceId();
  }

  async getAppSessionID(): Promise<string | null> {
    if (!nativeBridge.isRunningInNativeContainer) {
      return null;
    }
    return await nativeBridge.getAppSessionId();
  }
}

export const inAppAnalyticsService = new InAppAnalyticsService();
